// React Compiler opt-out: this component's own store read is bridged, but it renders
// SupplementTypeRenderer, which resolves its child through the supplement-type registry —
// a subtree (the 6 type renderers, reaching generic/MarkdownRendererV2 + generic/AudioPlayer)
// whose reads are not bridged. Compiling here would return cached JSX and cut the legacy
// forceUpdate cascade off for those descendants. Remove once client/generic is bridged
// (see flux-bridge-conversion skill, step 4).
'use no memo';

import {LoadingSpinner, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import AnnotatableSupplement from 'client/components/PracticeView/Annotations/AnnotatableSupplement/AnnotatableSupplement';
import {useQuestionTranslationStore} from 'client/components/QuestionTypes/shared/TranslationSection';
import {ALTERNATE_CONTENT_LANGUAGES} from 'client/components/QuestionTypes/shared/TranslationSection/Translation.types';
import supplementActions from 'client/Supplement/SupplementActions';
import supplementStore from 'client/Supplement/SupplementStore';
import {SupplementTypeRenderer} from 'client/SupplementTypes/SupplementTypeRenderer.react';
import {Map} from 'immutable';
import React, {useEffect, useMemo, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useQuestionEditorV2Store} from 'client/EditPage/V2/QuestionEditorV2Store';

import {callAction, useFluxStore} from '../framework';

interface SupplementRendererProps {
  id: string;
  attributes: unknown;
  /** False for answer-material hosts (chips, ordering items): no whole-supplement marks. */
  annotatable?: boolean;
}

// Poll cadence mirrors the SupplementEditor AI-generation poll: 3s interval, 3s initial delay.
const GENERATION_POLL_INTERVAL_MS = 3000;
// ponytail: ~3 min ceiling so a genuinely-failed generation stops polling and surfaces an error
// instead of looping forever in an open preview. Raise if generations legitimately take longer.
const MAX_GENERATION_POLL_ATTEMPTS = 60;

/**
 * True while the BE Oban job is still generating this supplement's asset. `tag_resolver` seeds
 * content.image / content.audio with a `pending/<uuid>` placeholder and overwrites it with the real
 * path on completion — and that path IS what the `<img>`/`<audio>` src is built from, so a `pending/`
 * path is exactly the broken-asset state we must wait out.
 *
 * ponytail: the path prefix is the sole signal — imageStatus/audioStatus are not exposed on the
 * public supplements_v1 endpoint this renders from (they default to 'complete'), so a status check
 * would be dead code. Add one only if this ever reads the authoring endpoint.
 */
interface GeneratableSupplement {
  getImageUrl?: () => string | undefined;
  getAudio?: () => string | undefined;
}

function isSupplementGenerating(supplement?: GeneratableSupplement | null): boolean {
  const image = supplement?.getImageUrl?.();
  const audio = supplement?.getAudio?.();
  const isPending = (path: unknown): boolean =>
    typeof path === 'string' && path.startsWith('pending/');
  return isPending(image) || isPending(audio);
}

const SupplementRenderer = ({id, attributes, annotatable = true}: SupplementRendererProps) => {
  // Subscribed (not a one-shot local snapshot) so an external ADD_SUPPLEMENT — e.g. the
  // SupplementEditor pushing a regenerated asset — re-renders the preview. See issue #19429.
  const supplement = useFluxStore(supplementStore, (s) =>
    s.hasSupplement(id) ? s.getSupplement(id) : null
  );
  const [hasError, setHasError] = useState(false);
  const currentTranslatedSupplements = useQuestionEditorV2Store((state) =>
    state.currentTranslatedSupplements()
  );

  useEffect(() => {
    if (supplement) {
      return;
    }
    let cancelled = false;
    // `getFetchSupplementPromise` hands back the store's single in-flight fetch, so without this
    // guard a rejected run for a previous `id` errors the *current* `id`.
    (async () => {
      callAction((supplementActions as Record<string, symbol>).FETCH_SUPPLEMENT, id, undefined);
      try {
        // The fetch commits to the store via ADD_SUPPLEMENT; the subscription re-renders us.
        await supplementStore.getFetchSupplementPromise();
      } catch (_err) {
        if (!cancelled) {
          setHasError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, supplement]);

  const isGenerating = useMemo(() => isSupplementGenerating(supplement), [supplement]);

  const hasFailed = hasError || (!!supplement && supplement.hasReadableContent?.() === false);

  // While the asset is still generating (placeholder `pending/` path), re-fetch the supplement until
  // the BE swaps in the real path, then re-render. Without this the FE shows the pending placeholder
  // (a broken image/audio) until a full page refresh. See issue #18861.
  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Self-scheduling timeout (not setInterval) so a slow request can't overlap the next tick.
    // Only a *resolved* fetch is committed; a still-pending one just schedules another attempt,
    // and the terminal-error branch is reached only when still pending — so success and error are
    // mutually exclusive even on the final attempt.
    const poll = async (attempt: number) => {
      let fresh: GeneratableSupplement | null | undefined;
      try {
        const query = resource('supplement_v1').mandarkEndpoint(['supplements_v1', id]);
        query.invalidateInterest(); // bypass the cached pending response
        fresh = (await query.getResourcePromise()) as GeneratableSupplement | null;
      } catch {
        // Swallow — the asset may not be ready yet; treat as still pending and retry.
      }
      if (cancelled) {
        return;
      }
      if (fresh && !isSupplementGenerating(fresh)) {
        // resolved → committing to the store re-renders us; isGenerating flips false and the effect tears down
        callAction((supplementActions as Record<string, symbol>).ADD_SUPPLEMENT, fresh);
        return;
      }
      if (attempt >= MAX_GENERATION_POLL_ATTEMPTS) {
        setHasError(true);
        return;
      }
      timer = setTimeout(() => {
        poll(attempt + 1);
      }, GENERATION_POLL_INTERVAL_MS);
    };

    // Delay the first poll so the BE Oban job has time to advance past the placeholder.
    timer = setTimeout(() => {
      poll(1);
    }, GENERATION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isGenerating, id]);

  // translation info
  const {translatedSupplements, showTranslations, showTranslatedSupplements} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        translatedSupplements: state.translatedSupplements,
        showTranslations: state.showTranslations,
        showTranslatedSupplements: state.translationSettings.supplements
      }))
    );

  const noTranslation = useMemo(() => {
    if (currentTranslatedSupplements) {
      const translatedSup = currentTranslatedSupplements.find((ts) => ts.id === id);
      return !(translatedSup?.meets_translation_requirements && translatedSup?.translate);
    }
    const translatedSup = translatedSupplements?.find((ts) => ts.id === id);
    return !(translatedSup?.meets_translation_requirements && translatedSup?.translate);
  }, [id, translatedSupplements, currentTranslatedSupplements]);

  // Named so a mark captured on translated text can never resolve against the original.
  const {content, name, lang} = useMemo(() => {
    if (!supplement) return {content: Map(), name: ''};
    if (currentTranslatedSupplements != null) {
      const translatedSup = currentTranslatedSupplements.find((ts) => ts.id === id);
      if (translatedSup) {
        const contentMap = Map({...translatedSup.content});
        return {
          content: contentMap,
          name: translatedSup.name,
          lang: ALTERNATE_CONTENT_LANGUAGES[0]
        };
      }
    }
    if (!showTranslatedSupplements) {
      return {
        content: supplement.getReadableContent?.() ?? Map(),
        name: supplement.get('name')
      };
    }
    if (showTranslations) {
      const translatedSup = translatedSupplements?.find((ts) => ts.id === id);
      if (translatedSup) {
        const contentMap = Map({...translatedSup.content});
        return {
          content: contentMap,
          name: translatedSup.name,
          lang: ALTERNATE_CONTENT_LANGUAGES[0]
        };
      }
    }
    return {
      content: supplement.getReadableContent?.() ?? Map(),
      name: supplement.get('name')
    };
  }, [
    supplement,
    showTranslations,
    id,
    translatedSupplements,
    currentTranslatedSupplements,
    showTranslatedSupplements
  ]);

  if (hasFailed) {
    // Mirrors the image supplement's own frame so a failed one occupies the same slot.
    return (
      <div className='background-subtle border-border-subtle rounded-x1 mb-x3 p-x3 min-h-x19 flex items-center justify-center border text-center'>
        <Text color='negative'>There was a problem loading the attached supplement.</Text>
      </div>
    );
  }

  if (!supplement || isGenerating) {
    return (
      <div className='flex justify-center'>
        <LoadingSpinner
          assistiveText={
            isGenerating ? 'Generating supplement...' : 'Loading question supplement...'
          }
          size={2}
        />
      </div>
    );
  }

  return (
    <AnnotatableSupplement
      supplementId={supplement.getId()}
      supplementType={supplement.get('type')}
      disabled={!annotatable}
      lang={lang ?? null}
      name={name}
    >
      <SupplementTypeRenderer
        supplementType={supplement.get('type')}
        id={supplement.getId()}
        name={name}
        content={content}
        supplement={supplement}
        attributes={attributes}
        noTranslation={noTranslation}
        showTranslationInfo={currentTranslatedSupplements != null || showTranslations}
        showTranslatedSupplements={
          currentTranslatedSupplements != null || showTranslatedSupplements
        }
      />
    </AnnotatableSupplement>
  );
};

export default SupplementRenderer;
